'use client';
import * as React from 'react';
import { Box } from '@mui/material';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Profile } from '@/shared/i18n';
import { EventBus } from '@/shared/utils';

import { PersonalInfo, NotificationSettings, DeleteAccount, FormActionButton } from './elements';
import { ProfileEventKeys, ProfileEvents } from '../events';
import { profileSchema, Profile as ProfileType } from '../types/profile';

interface ProfileFormProps {
  dict: Profile;
  eventBus: EventBus<ProfileEvents>;
  initialValues: ProfileType;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({ dict, eventBus, initialValues }) => {
  const { handleSubmit, control, setValue } = useForm<ProfileType>({
    defaultValues: initialValues,
    resolver: zodResolver(profileSchema),
  });

  const watchedValues = useWatch({ control });

  const isDirty = React.useMemo(() => {
    return JSON.stringify(watchedValues) !== JSON.stringify(initialValues);
  }, [watchedValues, initialValues]);

  const onSubmit: SubmitHandler<ProfileType> = React.useCallback(
    (data) => eventBus.emit(ProfileEventKeys.profile.update, data),
    [eventBus],
  );
  return (
    <div className="flex flex-col w-full gap-y-4 md:gap-y-6">
      <form action="" className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <PersonalInfo
            control={control}
            dict={dict.profile}
            setValue={setValue}
            profilePicture={initialValues.profilePicture}
          />
        </Box>

        <Box>
          <NotificationSettings control={control} dict={dict.profile} />
        </Box>

        <Box>
          <DeleteAccount eventBus={eventBus} dict={dict.profile} profile={initialValues} />
        </Box>

        <FormActionButton buttonText={dict.profile.save} disabled={!isDirty} />
      </form>
    </div>
  );
};
